import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import moment from "moment";
import Flatpickr from "react-flatpickr";
import { ArrowLeft } from "lucide-react";
import BreadCrumb from "Common/BreadCrumb";
import { useSlotDetails } from "./useGameSlot";
import { useGames } from "pages/games/useGames";
import { updateGameSlot } from "services/apiGameSlots";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";

function normalizeDrawDateToISO(drawDate: any): string {
  if (!drawDate) return "";
  if (typeof drawDate === "string") return drawDate;
  if (Array.isArray(drawDate) && drawDate.length >= 5) {
    const [y, mo, d, h, mi] = drawDate;
    const date = new Date(y, (mo ?? 1) - 1, d ?? 1, h ?? 0, mi ?? 0);
    return date.toISOString();
  }
  return "";
}

const EditGameSlot = () => {
  const { gameSlotId } = useParams<{ gameSlotId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { details, isLoading, error } = useSlotDetails(gameSlotId);
  const { games: gameList } = useGames();
  const [radioOption, setRadioOption] = useState<"percentage" | "amount">("percentage");

  const gamesArray: any[] = Array.isArray(gameList)
    ? gameList
    : ((gameList as { data?: any[] } | null)?.data ?? []);

  const slot = details?.gameslotdto ?? (details as any)?.data?.gameslotdto;
  const gameDisplayName =
    slot?.title ||
    gamesArray.find((g: any) => String(g?.id) === String(slot?.gameId ?? ""))?.title ||
    slot?.slotTitle ||
    "Unknown game";
  const winnerAmountsArr = slot?.winnerAmounts && Array.isArray(slot.winnerAmounts) ? slot.winnerAmounts : [];
  const hasWinnerAmounts = winnerAmountsArr.length > 0;
  const numWinnersFromSlot = slot?.numberOfWinners ?? winnerAmountsArr.length;

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      gameId: slot?.gameId ?? "",
      totalNumberOfTickets: slot?.totalNumberOfTickets ?? "",
      drawDate: slot ? normalizeDrawDateToISO(slot.drawDate) : "",
      numberOfWinners: numWinnersFromSlot ?? "",
      winPercentage: slot?.winnerPercentage ?? "",
      amountToBeWon: slot?.amountToBeWon ?? "",
      winnerAmount: slot?.winnerAmount ?? "",
      winnerAmounts: winnerAmountsArr.map((a: any) => String(a ?? "")) ?? [],
      questionSetId: "",
      questionSelectionType: "auto",
    },
    validationSchema: Yup.object({
      gameId: Yup.number().required("Please select a Game"),
      totalNumberOfTickets: Yup.number().required("Please Add Quantity"),
      winPercentage: Yup.number().max(50, "Max is 50%"),
      winnerAmount: Yup.number(),
      winnerAmounts: Yup.array().of(Yup.number().min(0)),
      drawDate: Yup.string()
        .required("Please Enter Start Date")
        .test("not-past-date", "Start date cannot be in the past", (value) =>
          moment(value).isSameOrAfter(moment(), "minute")
        ),
      numberOfWinners: Yup.number(),
    }),
    onSubmit: async (values) => {
      if (!gameSlotId) return;
      try {
        const n = Number(values.numberOfWinners) || 0;
        const amounts = (values.winnerAmounts as any[])?.slice(0, n).map((v: any) => (v === "" || v == null ? 0 : Number(v))) ?? [];
        const payload: any = {
          gameId: values.gameId,
          totalNumberOfTickets: values.totalNumberOfTickets,
          drawDate: values.drawDate,
          numberOfWinners: values.numberOfWinners,
          winnerPercentage: values.winPercentage,
          amountToBeWon: values.amountToBeWon,
          autoGenerateQuestions: true,
        };
        if (n > 0 && amounts.length === n && amounts.some((a: number) => a > 0)) {
          payload.winnerAmounts = amounts;
          payload.amountToBeWon = amounts.reduce((a: number, b: number) => a + b, 0);
        } else {
          payload.winnerAmount = values.winnerAmount ? Number(values.winnerAmount) : null;
        }
        await updateGameSlot(gameSlotId, payload);
        toast.success("Slot updated successfully");
        queryClient.invalidateQueries({ queryKey: ["slotDetails", gameSlotId] });
        queryClient.invalidateQueries({ queryKey: ["gameSlots"] });
        navigate(`/game-slots/details/${gameSlotId}`);
      } catch (err: any) {
        toast.error(err?.message || "Failed to update slot");
      }
    },
  });

  useEffect(() => {
    if (hasWinnerAmounts) setRadioOption("amount");
  }, [hasWinnerAmounts]);

  const numWinners = Number(validation.values.numberOfWinners) || 0;
  useEffect(() => {
    if (slot?.type === "RADIO" && numWinners > 0) {
      const current = (validation.values.winnerAmounts as any[]) || [];
      if (current.length !== numWinners) {
        const next = Array.from({ length: numWinners }, (_, i) =>
          current[i] !== undefined && current[i] !== "" ? current[i] : ""
        );
        validation.setFieldValue("winnerAmounts", next);
      }
    }
  }, [numWinners, slot?.type]);

  const selectedGame = gamesArray.find(
    (g: any) => String(g?.id) === String(validation.values.gameId ?? "")
  );
  const isRadioGame = selectedGame?.type === "RADIO";

  if (isLoading) return <p className="p-4 dark:text-white">Loading...</p>;
  if (error || !details) return <p className="p-4 text-red-500">Error loading slot or slot not found.</p>;
  if (!slot) return <p className="p-4 text-red-500">Slot data not available.</p>;

  return (
    <div className="py-2 dark:bg-gray-900 dark:text-white min-h-screen">
      <BreadCrumb title="Edit Slot" pageTitle="Game Slots" />
      <div className="flex items-center gap-2 my-5">
        <button
          type="button"
          className="flex items-center gap-2 dark:text-white"
          onClick={() => navigate(`/game-slots/details/${gameSlotId}`)}
        >
          <ArrowLeft className="w-5 h-5" />
          Back to details
        </button>
      </div>
      <div className="card dark:bg-gray-800">
        <div className="card-body">
          <h2 className="text-xl font-semibold mb-4">Edit Slot: {slot.slotTitle}</h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
            }}
            className="grid grid-cols-1 gap-4 xl:grid-cols-12"
          >
            <div className="xl:col-span-12">
              <label className="inline-block mb-2 text-base font-medium">Select Game</label>
              <select
                className="form-input border-slate-200 dark:border-zink-500 w-full dark:bg-zink-700 dark:border-zink-500"
                name="gameId"
                value={validation.values.gameId}
                disabled
              >
                <option value={slot?.gameId ?? ""}>{gameDisplayName}</option>
              </select>
              <p className="text-sm text-slate-500 mt-1">Game cannot be changed when editing.</p>
            </div>

            <div className="xl:col-span-12">
              <label className="inline-block mb-2 text-base font-medium">Total Number of Tickets</label>
              <input
                type="number"
                className="form-input border-slate-200 dark:border-zink-500 w-full dark:bg-zink-700"
                name="totalNumberOfTickets"
                value={validation.values.totalNumberOfTickets || ""}
                onChange={validation.handleChange}
              />
              {validation.touched.totalNumberOfTickets && validation.errors.totalNumberOfTickets && (
                <p className="text-red-400">{String(validation.errors.totalNumberOfTickets)}</p>
              )}
            </div>

            {isRadioGame && (
              <div className="xl:col-span-12">
                <label className="inline-block mb-2 text-base font-medium">Number of Winners for Radio Game</label>
                <input
                  type="number"
                  min={1}
                  max={5}
                  className="form-input border-slate-200 dark:border-zink-500 w-full dark:bg-zink-700"
                  name="numberOfWinners"
                  value={validation.values.numberOfWinners || ""}
                  onChange={validation.handleChange}
                />
                {validation.touched.numberOfWinners && validation.errors.numberOfWinners && (
                  <p className="text-red-400">{String(validation.errors.numberOfWinners)}</p>
                )}
              </div>
            )}

            <div className="xl:col-span-12">
              <label className="inline-block mb-2 text-base font-medium">Amount To Be Won (Incentive)</label>
              <input
                type="number"
                className="form-input border-slate-200 dark:border-zink-500 w-full dark:bg-zink-700"
                name="amountToBeWon"
                value={validation.values.amountToBeWon || ""}
                onChange={validation.handleChange}
              />
              {validation.touched.amountToBeWon && validation.errors.amountToBeWon && (
                <p className="text-red-400">{String(validation.errors.amountToBeWon)}</p>
              )}
            </div>

            {isRadioGame && (
              <div className="xl:col-span-6">
                <label className="inline-block mb-2 text-base font-medium">Select Reward Type</label>
                <div className="flex gap-4">
                  <label>
                    <input
                      type="radio"
                      name="rewardType"
                      checked={radioOption === "percentage"}
                      onChange={() => setRadioOption("percentage")}
                      className="mr-1"
                    />
                    Win Percentage
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="rewardType"
                      checked={radioOption === "amount"}
                      onChange={() => setRadioOption("amount")}
                      className="mr-1"
                    />
                    Winner Amount
                  </label>
                </div>
              </div>
            )}

            {(!isRadioGame || (isRadioGame && radioOption === "percentage")) && (
              <div className="xl:col-span-6">
                <label className="inline-block mb-2 text-base font-medium">Enter Win Percentage for Users (%)</label>
                <input
                  type="number"
                  max={50}
                  className="form-input border-slate-200 dark:border-zink-500 w-full dark:bg-zink-700"
                  name="winPercentage"
                  value={validation.values.winPercentage || ""}
                  onChange={validation.handleChange}
                />
              </div>
            )}

            {isRadioGame && numWinners > 0 && (
              <div className="xl:col-span-12">
                <label className="inline-block mb-2 text-base font-medium">Enter Winner Amount (per position)</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Array.from({ length: numWinners }, (_, i) => (
                    <div key={i}>
                      <label className="inline-block mb-1 text-sm font-medium">Winner {i + 1} amount</label>
                      <input
                        type="number"
                        min={0}
                        className="form-input border-slate-200 dark:border-zink-500 w-full dark:bg-zink-700"
                        value={(validation.values.winnerAmounts as any[])?.[i] ?? ""}
                        onChange={(e) => {
                          const arr = [...((validation.values.winnerAmounts as any[]) || [])];
                          arr[i] = e.target.value;
                          validation.setFieldValue("winnerAmounts", arr);
                        }}
                      />
                    </div>
                  ))}
                </div>
                {(() => {
                  const amounts = (validation.values.winnerAmounts as any[]) || [];
                  const sum = amounts.reduce((s: number, v: any) => s + (Number(v) || 0), 0);
                  return sum > 0 ? (
                    <p className="mt-2 text-sm">Total amount to be won: <strong>{sum.toLocaleString()}</strong></p>
                  ) : null;
                })()}
              </div>
            )}

            {isRadioGame && radioOption === "amount" && numWinners <= 0 && (
              <div className="xl:col-span-6">
                <label className="inline-block mb-2 text-base font-medium">Enter Winner Amount</label>
                <input
                  type="number"
                  className="form-input border-slate-200 dark:border-zink-500 w-full dark:bg-zink-700"
                  name="winnerAmount"
                  value={validation.values.winnerAmount || ""}
                  onChange={validation.handleChange}
                />
              </div>
            )}

            <div className="xl:col-span-12">
              <label className="inline-block mb-2 text-base font-medium">Draw Date & Time</label>
              <Flatpickr
                options={{
                  enableTime: true,
                  time_24hr: false,
                  dateFormat: "Y-m-d H:i:S",
                  altInput: true,
                  altFormat: "F j, Y at h:i K",
                  static: true,
                }}
                className="form-input border-slate-200 dark:border-zink-500 w-full dark:bg-zink-700"
                value={validation.values.drawDate || ""}
                onChange={([date]: any[]) => {
                  const d = date ? new Date(date.getTime() - date.getTimezoneOffset() * 60000) : null;
                  validation.setFieldValue("drawDate", d ? d.toISOString() : "");
                }}
              />
              {validation.touched.drawDate && validation.errors.drawDate && (
                <p className="text-red-400">{String(validation.errors.drawDate)}</p>
              )}
            </div>

            <div className="xl:col-span-12 flex gap-2">
              <button
                type="submit"
                className="text-white btn bg-custom-500 hover:bg-custom-600"
              >
                Update Slot
              </button>
              <Link to={`/game-slots/details/${gameSlotId}`} className="btn border-slate-200 dark:border-zink-500">
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditGameSlot;
